import {
  FileText,
  RefreshCw,
  AlertCircle,
  Inbox,
  Download,
  ExternalLink,
  Loader2,
} from "lucide-react";
import OrbitLoader from "../../../ui/components/OrbitLoader";
import useMyCase from "../useMyCase";
import useDocumentOpen, {isViewableDoc} from "../useDocumentOpen";
import {
  DOCUMENTS_COMING_SOON_MESSAGE,
  DOCUMENTS_DOWNLOAD_ENABLED,
} from "../documentAccess";
import {formatDate} from "../caseFormat";

const DHW_BLUE = "#001489";

// The participant's documents on file in the CRM (Jake's "document list in
// hamburger menu"). Tapping a row opens the file full-screen in the
// browser's viewer (PDF/image), or downloads it for non-viewable types;
// the backend document route re-checks ownership + the category allowlist.
const DocumentRow = ({doc, onOpen, docState, accessBlocked}) => {
  const added = formatDate(doc.active_date);
  const name = doc.name || doc.file_name || "Document";
  const busy = !accessBlocked && docState === "loading";
  const viewable = isViewableDoc(doc);
  const blockedLabel = `${name} — download not available yet`;
  return (
    <li>
      <button
        type="button"
        onClick={() => !accessBlocked && onOpen(doc)}
        disabled={busy || accessBlocked}
        title={accessBlocked ? DOCUMENTS_COMING_SOON_MESSAGE : undefined}
        aria-label={
          accessBlocked
            ? blockedLabel
            : `${viewable ? "Open" : "Download"} ${name}`
        }
        aria-describedby={accessBlocked ? "documents-coming-soon" : undefined}
        className={`flex w-full items-center gap-3 rounded-2xl border border-[#ECECEC] bg-white px-4 py-4 text-left focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-70 ${
          accessBlocked ? "cursor-not-allowed" : ""
        }`}
      >
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{backgroundColor: "#EEF1FB", color: DHW_BLUE}}
          aria-hidden="true"
        >
          <FileText size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[#191919]">
            {name}
          </p>
          <p className="text-sm text-slate-500">
            {added ? `Added ${added}` : "On file"}
          </p>
          {docState === "error" && (
            <p className="text-sm font-semibold text-rose-600">
              Could not open this document. Try again, or ask your case manager
              for a copy.
            </p>
          )}
        </div>
        <span className="shrink-0" style={{color: DHW_BLUE}} aria-hidden="true">
          {busy ? (
            <Loader2 size={20} className="animate-spin" />
          ) : viewable ? (
            <ExternalLink size={20} />
          ) : (
            <Download size={20} />
          )}
        </span>
      </button>
    </li>
  );
};

const MyDocuments = ({loggedInUser}) => {
  const {status, caseData, refetch} = useMyCase(loggedInUser);
  const {openDocument, docStates} = useDocumentOpen(loggedInUser?.customer_id);
  const documents = caseData?.documents ?? [];
  const accessBlocked = !DOCUMENTS_DOWNLOAD_ENABLED;

  return (
    <div
      data-testid="documents"
      className="min-h-full bg-white pb-10 font-sans"
    >
      {/* Intro sits under the app-chrome title ("My Documents") — don't
          repeat that heading here. */}
      <div className="mb-6">
        <p className="text-base text-slate-600">
          Papers your case manager has on file for you.
        </p>
      </div>

      {status === "loading" && (
        <div className="flex min-h-[40vh] items-center justify-center">
          <OrbitLoader />
        </div>
      )}

      {status === "error" && (
        <div className="mx-auto max-w-md rounded-[2rem] border border-rose-100 bg-rose-50 p-8 text-center">
          <AlertCircle
            size={40}
            className="mx-auto mb-3 text-rose-500"
            aria-hidden="true"
          />
          <h3 className="text-lg font-bold text-[#191919]">
            We could not load your documents
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Something went wrong. Please try again. If it keeps happening, call
            your case manager.
          </p>
          <button
            type="button"
            onClick={refetch}
            className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{backgroundColor: DHW_BLUE}}
          >
            <RefreshCw size={16} aria-hidden="true" />
            Try again
          </button>
        </div>
      )}

      {status === "success" && documents.length === 0 && (
        <div className="mx-auto max-w-md rounded-[2rem] border border-slate-100 bg-slate-50 p-8 text-center">
          <Inbox
            size={40}
            className="mx-auto mb-3 text-slate-400"
            aria-hidden="true"
          />
          <h3 className="text-lg font-bold text-[#191919]">No documents yet</h3>
          <p className="mt-1 text-sm text-slate-600">
            When your case manager adds papers to your file, they will show up
            here.
          </p>
        </div>
      )}

      {status === "success" && documents.length > 0 && (
        <>
          {accessBlocked && (
            <p
              id="documents-coming-soon"
              className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600"
            >
              {DOCUMENTS_COMING_SOON_MESSAGE} Need a copy now? Ask your case
              manager.
            </p>
          )}
          <ul className="space-y-3">
            {documents.map((doc, i) => (
              <DocumentRow
                key={doc.document_id || i}
                doc={doc}
                onOpen={openDocument}
                docState={docStates[doc.document_id]}
                accessBlocked={accessBlocked}
              />
            ))}
          </ul>
          {!accessBlocked && (
            <p className="mt-4 text-sm text-slate-500">
              Tap a document to open it. Need help? Ask your case manager.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default MyDocuments;
